import React, {useEffect, useRef, useState} from 'react';
import {Box, ButtonBase, Stack, Typography, IconButton, Menu, MenuItem} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {HugeiconsIcon} from '@hugeicons/react';
import {
  ArrowLeft01Icon, Delete01Icon, MoreVerticalIcon, PencilEdit01Icon, DatabaseIcon, File02Icon, Message01Icon, Layers01Icon, Settings02Icon,
  GlobeIcon, Calendar03Icon, Clock01Icon, UserIcon, FlashIcon,
} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';
import {KnowledgeManagerRepository} from '../../../../repositories/KnowledgeManagerRepository';
import {ConfirmModalNew} from '../../mcp-discovery/components/ConfirmModalNew';
import {selectedTopicAtom, documentsAtom, projectAtom, queriesAtom} from '../atoms';
import {EditTopicModalNew} from './EditTopicModalNew';
import {DocumentsTab} from './DocumentsTab';
import {QueryTab} from './QueryTab';
import {GraphTab} from './GraphTab';
import {SettingsTab} from './SettingsTab';
import {hexToRgba} from './TopicGallery';
import {subProjectLabel} from '../../../../utils/subProjects';

const CARD_SHADOW = "0 2px 12px rgba(35, 38, 77, 0.07)";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
};

const Section = ({id, title, count, children}) => (
  <Box id={id} sx={{scrollMarginTop: '24px'}}>
    <Stack direction="row" alignItems="baseline" gap={1.25} sx={{mb: 1.75}}>
      <Typography component="h3" sx={{fontSize: 20, fontWeight: 700, color: '#23264D', letterSpacing: '-0.02em'}}>{title}</Typography>
      {count !== undefined && count !== null && (
        <Box component="span" sx={{px: 1.1, py: 0.2, borderRadius: '999px', fontSize: 12, fontWeight: 600, color: '#4B55C9', bgcolor: 'rgba(132, 145, 247, 0.14)'}}>
          {count}
        </Box>
      )}
    </Stack>
    <Box sx={{bgcolor: '#fff', borderRadius: '20px', boxShadow: CARD_SHADOW, p: {xs: 2, md: 3}, minWidth: 0}}>{children}</Box>
  </Box>
);

// The graph is heavy, so it is only mounted once its section has scrolled into view
const LazyMount = ({children, minHeight = 200}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible || !ref.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) setVisible(true);
    }, {rootMargin: '200px'});
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  return <Box ref={ref} sx={{minHeight: visible ? 0 : minHeight}}>{visible ? children : null}</Box>;
};

// "On this page" rail: jumps to a section and highlights the one being read
const SectionRail = ({items}) => {
  const [active, setActive] = useState(items[0].id);

  useEffect(() => {
    const update = () => {
      let current = items[0].id;
      items.forEach(({id}) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 160) current = id;
      });
      setActive(current);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [items]);

  return (
    <Box component="nav" aria-label="Sections" sx={{position: {md: 'sticky'}, top: {md: 24}, alignSelf: 'start', minWidth: 0, maxWidth: '100%'}}>
      <Typography sx={{display: {xs: 'none', md: 'block'}, px: 1.5, pb: 1, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8491F7'}}>
        On this page
      </Typography>
      <Stack gap={0.25} sx={{flexDirection: {xs: 'row', md: 'column'}, overflowX: {xs: 'auto', md: 'visible'}, scrollbarWidth: 'none', '&::-webkit-scrollbar': {display: 'none'}, pb: {xs: 0.5, md: 0}}}>
        {items.map(({id, label, icon, count}) => {
          const selected = active === id;
          return (
            <ButtonBase
              key={id} aria-current={selected}
              onClick={() => document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'})}
              sx={{
                width: {md: '100%'}, flexShrink: 0, whiteSpace: 'nowrap', justifyContent: 'flex-start', gap: 1.25, height: {xs: 38, md: 42}, px: 1.5, borderRadius: '12px', fontSize: 14,
                fontWeight: selected ? 700 : 500, textAlign: 'left', color: selected ? '#4B55C9' : '#23264D',
                bgcolor: selected ? 'rgba(132, 145, 247, 0.14)' : 'transparent', transition: 'background-color .15s ease',
                '&:hover': {bgcolor: selected ? 'rgba(132, 145, 247, 0.14)' : '#fff'},
              }}
            >
              <Box sx={{display: 'flex', flexShrink: 0}}><HugeiconsIcon icon={icon} size={18} strokeWidth={1.5}/></Box>
              <Box component="span" sx={{flex: 1}}>{label}</Box>
              {count !== undefined && count !== null && (
                <Box component="span" sx={{fontSize: 11, fontWeight: 600, color: selected ? '#4B55C9' : '#8A8AA0'}}>{count}</Box>
              )}
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
};

export const TopicDetailsPanel = ({onTopicChanged}) => {
  const [selectedTopic, setSelectedTopic] = useAtom(selectedTopicAtom);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const documents = useAtomValue(documentsAtom);
  const [queries, setQueries] = useAtom(queriesAtom);
  const project = useAtomValue(projectAtom);
  const topRef = useRef(null);

  useEffect(() => {
    if (!selectedTopic?.id) {
      setQueries([]);
      return;
    }
    let cancelled = false;
    KnowledgeManagerRepository.listQueries(selectedTopic.id, {limit: 50})
      .then(list => {if (!cancelled) setQueries(list);})
      .catch(() => {if (!cancelled) setQueries([]);});
    return () => {cancelled = true;};
  }, [selectedTopic?.id, setQueries]);

  useEffect(() => {
    topRef.current?.scrollIntoView({block: 'start'});
  }, [selectedTopic?.id]);

  if (!selectedTopic) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await KnowledgeManagerRepository.deleteTopic(selectedTopic.id);
      toast.success("Topic deleted");
      setSelectedTopic(null);
      setConfirmOpen(false);
      await onTopicChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete topic");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSaved = async (updated) => {
    setSelectedTopic(updated);
    setEditOpen(false);
    await onTopicChanged?.();
  };

  const subProject = (project?.subProjects || []).find(
    item => String(item.id) === String(selectedTopic.subProjectId),
  );
  const topicDocuments = documents.filter(d => d.topicId === selectedTopic.id);
  const documentsValue = topicDocuments.length > 0
    ? `${topicDocuments.filter(d => d.status === 'indexed').length} indexed`
    : `${selectedTopic.documentCount || 0} documents`;

  const topicQueries = queries.filter(q => q.topicId === selectedTopic.id);
  const timedQueries = topicQueries.filter(q => !!q.processingTimeMs);
  const avgResponseTimeMs = timedQueries.length > 0
    ? timedQueries.reduce((sum, q) => sum + q.processingTimeMs, 0) / timedQueries.length
    : null;

  const facts = [
    subProject ? {icon: GlobeIcon, label: 'Sub project', value: subProjectLabel(subProject)} : null,
    {icon: File02Icon, label: 'Documents', value: documentsValue},
    avgResponseTimeMs != null ? {icon: FlashIcon, label: 'Avg response', value: `${(avgResponseTimeMs / 1000).toFixed(1)}s`} : null,
    {icon: Calendar03Icon, label: 'Created', value: formatDate(selectedTopic.createdAt)},
    {icon: Clock01Icon, label: 'Updated', value: formatDate(selectedTopic.updatedAt)},
    selectedTopic.createdBy ? {icon: UserIcon, label: 'Created by', value: selectedTopic.createdBy} : null,
  ].filter(Boolean);

  return (
    <Box ref={topRef} sx={{pb: 8, scrollMarginTop: '16px'}}>
      <Stack direction="row" alignItems="center" gap={1.25} sx={{mb: 2}}>
        <IconButton
          onClick={() => setSelectedTopic(null)} aria-label="Back to knowledge base"
          sx={{
            width: 38, height: 38, bgcolor: '#fff', color: '#23264D', boxShadow: '0 2px 12px rgba(35, 38, 77, 0.07)',
            transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background-color .15s ease',
            '&:hover': {bgcolor: '#fff', transform: 'translateX(-3px)'},
          }}
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={18}/>
        </IconButton>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8491F7'}}>Knowledge base</Typography>
      </Stack>

      <Box sx={{position: 'relative', borderRadius: '24px', p: {xs: 2.5, md: 3.5}, background: 'linear-gradient(120deg, #E9ECFF 0%, #F3E9FA 55%, #FCE9F1 100%)'}}>
      <Stack direction="row" alignItems="flex-start" gap={2.25} flexWrap="wrap">
        <Box sx={{
          width: 64, height: 64, borderRadius: '20px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#fff', boxShadow: CARD_SHADOW, color: selectedTopic.color || '#8491F7',
        }}>
          <HugeiconsIcon icon={DatabaseIcon} size={28}/>
        </Box>
        <Box sx={{flex: '1 1 280px', minWidth: 0, pr: {md: 6}}}>
          <Typography component="h2" sx={{fontSize: {xs: 24, md: 32}, fontWeight: 700, color: '#23264D', letterSpacing: '-0.03em', lineHeight: 1.1}}>
            {selectedTopic.name}
          </Typography>
          {selectedTopic.description && (
            <Typography sx={{fontSize: 14, color: '#6B6B80', mt: 0.75, maxWidth: '72ch'}}>{selectedTopic.description}</Typography>
          )}
        </Box>
        <Box sx={{position: 'absolute', top: {xs: 14, md: 20}, right: {xs: 14, md: 20}}}>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions" sx={{bgcolor: 'rgba(255, 255, 255, 0.7)', '&:hover': {bgcolor: '#fff'}}}>
            <HugeiconsIcon icon={MoreVerticalIcon} size={18}/>
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{sx: {borderRadius: '12px'}}}>
            <MenuItem onClick={() => {setMenuAnchor(null); setEditOpen(true);}} sx={{gap: 1}}>
              <HugeiconsIcon icon={PencilEdit01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Edit Topic</Typography>
            </MenuItem>
            <MenuItem onClick={() => {setMenuAnchor(null); setConfirmOpen(true);}} sx={{color: '#DC2626', gap: 1}}>
              <HugeiconsIcon icon={Delete01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Delete Topic</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Stack>

      <Box sx={{
        mt: 3, p: 2.5, borderRadius: '18px', bgcolor: 'rgba(255, 255, 255, 0.6)', display: 'grid', gap: 2.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      }}>
        {facts.map(({icon, label, value}) => (
          <Box key={label} sx={{minWidth: 0}}>
            <Stack direction="row" alignItems="center" gap={0.6} sx={{color: '#8A8AA0'}}>
              <HugeiconsIcon icon={icon} size={13}/>
              <Typography sx={{fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase'}}>{label}</Typography>
            </Stack>
            <Typography noWrap title={String(value)} sx={{fontSize: 14, fontWeight: 600, color: '#23264D', mt: 0.5}}>{value}</Typography>
          </Box>
        ))}
        {(selectedTopic.tags || []).length > 0 && (
          <Box sx={{minWidth: 0, gridColumn: '1 / -1'}}>
            <Typography sx={{fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A8AA0', mb: 0.75}}>Tags</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {selectedTopic.tags.map(tag => (
                <Box key={tag} component="span" sx={{px: 1.25, py: 0.4, borderRadius: '8px', fontSize: 12, fontWeight: 500, color: '#4B55C9', bgcolor: hexToRgba('#8491F7', 0.16)}}>
                  {tag}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
      </Box>

      <Box sx={{mt: {xs: 2.5, md: 4}, display: 'grid', gap: {xs: 2.5, md: 4}, gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: '200px minmax(0, 1fr)'}}}>
        <SectionRail items={[
          {id: 'kb-documents', label: 'Documents', icon: File02Icon, count: selectedTopic.documentCount || 0},
          {id: 'kb-query', label: 'Query', icon: Message01Icon, count: selectedTopic.queryCount || null},
          {id: 'kb-graph', label: 'Knowledge Graph', icon: Layers01Icon},
          {id: 'kb-settings', label: 'Settings', icon: Settings02Icon},
        ]}/>

        <Stack gap={{xs: 3.5, md: 5}} sx={{minWidth: 0}}>
          <Section id="kb-documents" title="Documents" count={selectedTopic.documentCount || 0}>
            <DocumentsTab topic={selectedTopic} onDocumentCountChanged={onTopicChanged}/>
          </Section>
          <Section id="kb-query" title="Query">
            <QueryTab topic={selectedTopic}/>
          </Section>
          <Section id="kb-graph" title="Knowledge Graph">
            <LazyMount minHeight={480}><GraphTab topic={selectedTopic}/></LazyMount>
          </Section>
          <Section id="kb-settings" title="Settings">
            <SettingsTab topic={selectedTopic}/>
          </Section>
        </Stack>
      </Box>

      <ConfirmModalNew
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title={`Delete "${selectedTopic.name}"?`}
        description="This will permanently remove the topic and all its documents. This action cannot be undone."
        confirmText="Delete Topic"
        loading={deleting}
      />

      <EditTopicModalNew open={editOpen} onClose={() => setEditOpen(false)} onSaved={handleEditSaved} topic={selectedTopic}/>
    </Box>
  );
};
